import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { GuidePricingHistoryEntry } from '@/types';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { dashboardPanelSx } from '@/shared/styles/surfaces';

interface PricingHistoryTableProps {
  items: GuidePricingHistoryEntry[];
  isLoading: boolean;
  error: Error | null;
}

function formatChangedAt(changedAt: string | undefined) {
  if (!changedAt) return '—';
  const date = new Date(changedAt);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PricingHistoryTable({ items, isLoading, error }: PricingHistoryTableProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to load pricing history.
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
        No pricing changes yet. Your first save will appear here.
      </Typography>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...dashboardPanelSx, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell align="right">Hourly</TableCell>
            <TableCell align="right">Full day</TableCell>
            <TableCell>Currency</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>{formatChangedAt(entry.changedAt)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatGuidePrice(entry.hourlyPrice, entry.currency)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatGuidePrice(entry.fullDayPrice, entry.currency)}
              </TableCell>
              <TableCell>{entry.currency}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
